// Pure helpers for the Knowledge Atlas renderer: color math and LOD ramps.
// Kept separate from the page so the deck.gl wiring stays readable.
//
// The Atlas reads as a MUNICIPAL PLANNING ARTIFACT on a light canvas (GIS /
// zoning map / blueprint / Figma), not a sci-fi galaxy. Colors stay muted and
// crisp; everything lifts gently off a near-white paper.

export type RGBA = [number, number, number, number];

/** Parse "#RRGGBB" (or "#RGB") into an [r,g,b] tuple, with a calm slate fallback. */
export function hexToRgb(hex: string | undefined): [number, number, number] {
  if (!hex) return [91, 124, 153];
  let h = hex.replace('#', '').trim();
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (h.length !== 6) return [91, 124, 153];
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return [91, 124, 153];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Desaturate a color toward graphite so districts read as muted, premium
 * zoning tints rather than bright primaries. `amount` 0..1 = how much to pull
 * toward gray; `dim` scales final brightness.
 */
export function muted(
  [r, g, b]: [number, number, number],
  amount = 0.45,
  dim = 0.85
): [number, number, number] {
  const gray = 0.299 * r + 0.587 * g + 0.114 * b;
  const mix = (c: number) => (c * (1 - amount) + gray * amount) * dim;
  return [Math.round(mix(r)), Math.round(mix(g)), Math.round(mix(b))];
}

/** Smoothstep easing between edge0 and edge1 -> 0..1. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** Cubic ease-out, for camera transitions and the load fade. */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Level-of-detail opacity ramps driven by zoom. Districts (zoning fill +
 * border + label) and the downtown core are ALWAYS visible so the "this
 * knowledge has been mapped" read works in 3 seconds even zoomed all the way
 * out. Connectors then landmarks fade in as you zoom in; landmark labels last.
 * Ramps are smooth and restrained, never hard toggles.
 *
 * OrthographicView zoom is roughly: -4 (very far) .. 3 (very close).
 */
export function lodFromZoom(zoom: number) {
  return {
    // Zoning regions + their labels are always present (full strength at all
    // zooms; the planning map signposting must read instantly).
    districts: 1,
    // The downtown core is the civic anchor: always rendered.
    capital: 1,
    // Connector network traces in across the mid range.
    roads: smoothstep(-1.8, -0.4, zoom),
    // Landmark map nodes appear slightly after the connectors.
    landmarks: smoothstep(-1.9, -0.6, zoom),
    // Document titles only when genuinely close.
    landmarkLabels: smoothstep(-0.2, 0.9, zoom),
    // Document INFO CARDS: a higher detail level still. As the user zooms IN
    // past this band, the small landmark dots cross-fade into elegant Figma/
    // Linear info cards (title, type, chunk + relationship counts). They ramp in
    // ABOVE the landmark-label band so dots/labels own the mid range and cards
    // own the close range, with a smooth opacity cross-fade between dot and card
    // across the threshold. Tuned alongside VIS.card.* in KnowledgeAtlas.tsx.
    cards: smoothstep(0.8, 1.6, zoom),
    // On-map cross-district theme labels: a mid-zoom band. They fade IN as the
    // structure becomes legible, then fade OUT again once the user is close
    // enough that per-document titles (landmarkLabels) take over — so the map
    // narrates itself at a glance but never clutters when zoomed all the way in.
    edgeLabels: smoothstep(-2.0, -1.1, zoom) * (1 - smoothstep(0.2, 0.9, zoom)),
  };
}

/** Clamp helper. */
export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * Darken a color toward black by `amount` (0..1). Used to give zoning borders
 * and map-node strokes a crisp, slightly-deeper edge than their fill.
 */
export function darken(
  [r, g, b]: [number, number, number],
  amount: number
): [number, number, number] {
  const drop = (c: number) => Math.round(c * (1 - amount));
  return [drop(r), drop(g), drop(b)];
}

/**
 * Shorten a document title for the always-visible downtown-core label.
 * Municipal codes get a clean civic name; anything else is truncated.
 */
export function shortenCoreLabel(title: string, maxLen = 24): string {
  if (!title) return 'Municipal Code';
  if (/code of ordinances|municipal code/i.test(title)) return 'Municipal Code';
  if (title.length <= maxLen) return title;
  return `${title.slice(0, maxLen - 1).trimEnd()}…`;
}
